<template>
  <div class="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
    <div class="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center transform transition-all">
      
      <!-- Loading State -->
      <div v-if="!error" class="flex flex-col items-center space-y-6 py-4">
        <div class="relative">
          <div class="animate-spin rounded-full h-12 w-12 border-4 border-primary-100 border-b-primary-600"></div>
        </div>
        <div class="space-y-2">
          <h2 class="text-xl font-semibold text-gray-900">Authenticating</h2>
          <p class="text-sm text-gray-500">Please wait while we securely log you in...</p>
        </div>
      </div>

      <!-- Error State -->
      <div v-else class="flex flex-col items-center space-y-4 py-2">
        <div class="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center text-red-600 mb-2">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </div>
        <div class="space-y-2">
          <h3 class="text-lg font-medium text-gray-900">Authentication Failed</h3>
          <p class="text-sm text-gray-500">{{ error }}</p>
        </div>
        <button 
          @click="goToLogin"
          class="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
        >
          Return to Login
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import { onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

// *** CRITICAL FIX: Changed absolute alias (@/) to relative path (../). ***
// This bypasses module resolution failures in production environments.
import { authService } from '../services/authService'; 

export default {
  name: 'AuthCallback',
  setup() {
    const store = useStore();
    const router = useRouter();
    const error = ref(null);

    const goToLogin = () => {
      router.push('/login');
    };

    const handleCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const errorMsg = params.get('error');

      if (errorMsg) {
        error.value = 'Failed to authenticate with Google.';
        console.error('[AuthCallback.vue] Google Auth Error:', errorMsg);
        // Increased timeout to give user time to read the error
        setTimeout(() => router.push('/login'), 5000);
        return;
      }

      if (code) {
        try {
          const { accessToken, refreshToken } = await authService.handleAuthCallback(code);
          
          await store.dispatch('auth/handleAuthResponse', { accessToken, refreshToken });
          
          const redirect = params.get('redirect') || '/portfolios';
          router.replace(redirect);
        
        } catch (err) {
          // This should capture the network/API errors.
          console.error('[AuthCallback.vue] CRASH/ERROR during API call:', err.message, err.response?.data);
          error.value = 'Failed to complete authentication. Please try again.';
          // Increased timeout to give user time to read the error
          setTimeout(() => router.push('/login?error=auth_failed'), 5000);
        }
      } else {
        console.warn('[AuthCallback.vue] No code or error found. Redirecting to login.');
        router.push('/login');
      }
    };

    onMounted(() => {
      handleCallback();
    });

    return { 
      error,
      goToLogin
    };
  }
};
</script>
